import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, ArrowLeft, ArrowRight, FilePdf, PresentationChart } from "@phosphor-icons/react";

export default function PitchDeckTab({ doc }) {
  const pack = doc.pack;
  const slides = pack?.pitch_deck?.slides || [];
  const [i, setI] = useState(0);

  if (!pack || slides.length === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-12 text-center card-elev">
        <div className="font-display text-xl tracking-tight mb-2">No slides yet</div>
        <p className="text-sm text-muted-foreground">Run AI Analysis to generate the institutional pitch deck.</p>
      </div>
    );
  }

  const s = slides[i];
  const download = async (fmt) => {
    const t = toast.loading(`Preparing ${fmt.toUpperCase()}…`);
    try {
      const resp = await api.get(`/investment-docs/${doc.id}/export/pitch-deck.${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `${(doc.company_name || "orixa-invest").replace(/\s+/g, "-").toLowerCase()}-pitch-deck.${fmt}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`, { id: t });
    } catch { toast.error("Download failed", { id: t }); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-testid="idoc-tab-pitch-body">
      <div className="lg:col-span-3">
        {/* Slide viewer */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden card-elev-lg aspect-video relative flex flex-col" data-testid="pitch-slide">
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary" />
          <div className="p-8 sm:p-12 flex-1 flex flex-col">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[hsl(var(--gold))] mb-4">
              SLIDE {String(i + 1).padStart(2, "0")} · {slides.length}
            </div>
            <div className="font-display text-3xl sm:text-4xl tracking-tight text-primary leading-tight max-w-3xl">{s.title || s.key}</div>
            {s.subtitle && <div className="mt-3 text-lg italic text-primary/70 max-w-3xl">{s.subtitle}</div>}
            {s.key === "cover" && pack.investment_thesis_headline && (
              <div className="mt-4 text-base italic text-primary/70 max-w-3xl">{pack.investment_thesis_headline}</div>
            )}
            {Array.isArray(s.body) && s.body.length > 0 && (
              <ul className="mt-8 space-y-3 text-base text-foreground max-w-3xl">
                {s.body.map((b, ii) => (
                  <li key={ii} className="flex gap-3"><span className="text-primary font-bold">•</span><span>{b}</span></li>
                ))}
              </ul>
            )}
            <div className="mt-auto pt-8 text-xs text-muted-foreground">
              {doc.company_name} · Prepared by Orixa Invest · Ibn Miyeta Group · Confidential
            </div>
          </div>
        </div>

        {/* Nav */}
        <div className="mt-4 flex items-center justify-between">
          <button
            onClick={() => setI((x) => Math.max(0, x - 1))} disabled={i === 0}
            className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2 text-sm font-semibold hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="pitch-prev"
          >
            <ArrowLeft size={14} /> Previous
          </button>
          <div className="text-xs text-muted-foreground">{i + 1} / {slides.length}</div>
          <button
            onClick={() => setI((x) => Math.min(slides.length - 1, x + 1))} disabled={i === slides.length - 1}
            className="inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2 text-sm font-semibold hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="pitch-next"
          >
            Next <ArrowRight size={14} />
          </button>
        </div>

        {/* Thumbnails */}
        <div className="mt-6 grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="pitch-thumbnails">
          {slides.map((sl, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              className={`text-left aspect-video rounded-md border transition-all p-2 ${
                idx === i ? "border-primary bg-teal-soft ring-2 ring-primary/20" : "border-border bg-card hover:border-primary/40"
              }`}
              data-testid={`pitch-thumb-${idx}`}
            >
              <div className="text-[8px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Slide {idx + 1}</div>
              <div className="text-[9px] font-semibold leading-tight line-clamp-3 text-foreground">{sl.title || sl.key}</div>
            </button>
          ))}
        </div>
      </div>

      <aside className="lg:col-span-1 lg:sticky lg:top-6 self-start">
        <div className="bg-card border border-border rounded-2xl p-5 card-elev">
          <div className="text-overline text-muted-foreground mb-3">Downloads</div>
          <button onClick={() => download("pdf")} className="w-full mb-2 inline-flex items-center justify-between gap-2 border border-border rounded-lg px-3 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="pitch-download-pdf">
            <span className="inline-flex items-center gap-2"><FilePdf size={16} weight="duotone" className="text-primary" /> PDF</span>
            <DownloadSimple size={14} weight="bold" />
          </button>
          <button onClick={() => download("pptx")} className="w-full inline-flex items-center justify-between gap-2 border border-border rounded-lg px-3 py-2.5 text-sm font-semibold hover:bg-secondary" data-testid="pitch-download-pptx">
            <span className="inline-flex items-center gap-2"><PresentationChart size={16} weight="duotone" className="text-primary" /> PPTX</span>
            <DownloadSimple size={14} weight="bold" />
          </button>
          <div className="mt-4 pt-4 border-t border-border text-[11px] text-muted-foreground leading-relaxed">
            <strong>Google Slides:</strong> download the PPTX, then import it into Google Slides via <em>File → Import Slides</em>.
          </div>
        </div>
      </aside>
    </div>
  );
}
